// AGC: PaymentSimulation Component
// AGC: Simulates payment flow for PRO/PREMIUM tier upgrades
// AGC: Placeholder for future Mercado Pago / PayPal integration

import React, { useState } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import { useAuth } from '../../hooks/useAuth'
import { 
  CreditCard, 
  CheckCircle, 
  Loader2, 
  Shield,
  ArrowLeft,
  Crown,
  Star,
  X,
  Smartphone,
  Building,
  Sparkles,
} from 'lucide-react'

/**
 * AGC: Payment simulation props
 */
interface PaymentSimulationProps {
  tier: 'pro' | 'premium'
  billingCycle: 'monthly' | 'annual'
  onSuccess: (result: PaymentResult) => void
  onCancel: () => void
  onBack: () => void
}

/**
 * AGC: Payment result interface
 */
interface PaymentResult {
  success: boolean
  tier: string
  transactionId: string
  amount: number
  currency: string
}

/**
 * AGC: Pricing data - using translations
 */
const getPricing = (t: (key: string) => string) => ({
  pro: {
    name: t('subscription.tierNames.pro') || 'PRO',
    displayName: t('subscription.tierNames.proDisplay') || 'Produtor Profissional',
    monthly: 99,
    annual: 990,
    annualSavings: 198,
    color: 'blue',
    icon: Star,
  },
  premium: {
    name: 'PREMIUM',
    displayName: t('subscription.tierNames.premiumDisplay') || 'Produtor Enterprise',
    monthly: 299,
    annual: 2990,
    annualSavings: 598,
    color: 'yellow',
    icon: Crown,
  },
})

/**
 * AGC: Payment methods available - using translations
 */
const getPaymentMethods = (t: (key: string) => string) => [
  { id: 'pix', name: t('subscription.payment.pix') || 'PIX', icon: Smartphone, description: t('subscription.payment.pixDescription') || 'Pagamento instantâneo' },
  { id: 'credit_card', name: t('subscription.payment.creditCard') || 'Cartão de Crédito', icon: CreditCard, description: t('subscription.payment.creditCardDescription') || 'Visa, Mastercard, Elo' },
  { id: 'boleto', name: t('subscription.payment.boleto') || 'Boleto Bancário', icon: Building, description: t('subscription.payment.boletoDescription') || 'Vencimento em 3 dias úteis' },
]

/**
 * AGC: PaymentSimulation Component
 */
export const PaymentSimulation: React.FC<PaymentSimulationProps> = ({
  tier,
  billingCycle,
  onSuccess,
  onCancel,
  onBack,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  
  const [step, setStep] = useState<'select' | 'processing' | 'success'>('select')
  const [selectedMethod, setSelectedMethod] = useState<string>('pix')
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const pricing = getPricing(t)[tier]
  const price = billingCycle === 'annual' ? pricing.annual : pricing.monthly
  const TierIcon = pricing.icon
  const paymentMethods = getPaymentMethods(t)

  // AGC: Simulate payment processing
  const handlePayment = async () => {
    setIsProcessing(true)
    setError(null)
    setStep('processing')

    try {
      // AGC: Call backend to simulate payment and upgrade
      const response = await fetch('/api/v1/subscription/upgrade', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user?.uid || '',
        },
        body: JSON.stringify({
          userId: user?.uid,
          tier,
          billingCycle,
          paymentMethod: selectedMethod,
        }),
      })

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || t('subscription.payment.paymentFailed'))
      }

      // AGC: Simulate processing delay for realism
      await new Promise(resolve => setTimeout(resolve, 2000))

      setStep('success')
      
      // AGC: Notify parent of success after animation
      setTimeout(() => {
        onSuccess({
          success: true,
          tier,
          transactionId: data.payment.transactionId,
          amount: price,
          currency: 'BRL',
        })
      }, 2000)

    } catch (err) {
      console.error('❌ AGC: Payment error:', err)
      setError(err instanceof Error ? err.message : t('subscription.payment.paymentError'))
      setStep('select')
    } finally {
      setIsProcessing(false)
    }
  }

  // AGC: Render processing state
  if (step === 'processing') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[90]">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md w-full mx-4 text-center">
          <div className="mb-6">
            <Loader2 className="w-16 h-16 animate-spin text-blue-500 mx-auto" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {t('subscription.payment.processing')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {t('subscription.payment.processingMessage')}
          </p>
          <div className="mt-6 flex items-center justify-center space-x-2 text-sm text-gray-500">
            <Shield className="w-4 h-4 text-green-500" />
            <span>{t('subscription.payment.secure')}</span>
          </div>
        </div>
      </div>
    )
  }

  // AGC: Render success state
  if (step === 'success') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[90]">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md w-full mx-4 text-center">
          <div className="mb-6">
            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {t('subscription.payment.success')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {t('subscription.payment.welcomeToPlan').replace('{plan}', pricing.name)}
          </p>
          <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full ${
            tier === 'pro' 
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' 
              : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
          }`}>
            <TierIcon className="w-5 h-5" />
            <span className="font-semibold">{pricing.displayName}</span>
          </div>
          <div className="mt-6">
            <Sparkles className="w-6 h-6 text-yellow-500 animate-pulse mx-auto" />
          </div>
        </div>
      </div>
    )
  }

  // AGC: Render payment method selection
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[90] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* AGC: Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('subscription.payment.back')}</span>
          </button>
          <button
            onClick={onCancel}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* AGC: Plan Summary */}
          <div className={`p-4 rounded-lg mb-6 ${
            tier === 'pro' 
              ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800' 
              : 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
          }`}>
            <div className="flex items-center space-x-3 mb-3">
              <div className={`p-2 rounded-full ${
                tier === 'pro' 
                  ? 'bg-blue-100 dark:bg-blue-900/50' 
                  : 'bg-yellow-100 dark:bg-yellow-900/50'
              }`}>
                <TierIcon className={`w-6 h-6 ${
                  tier === 'pro' 
                    ? 'text-blue-600 dark:text-blue-400' 
                    : 'text-yellow-600 dark:text-yellow-400'
                }`} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {pricing.displayName}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {billingCycle === 'annual' ? t('subscription.payment.annualPlan') : t('subscription.payment.monthlyPlan')}
                </p>
              </div>
            </div>
            <div className="flex items-end justify-between">
              <div>
                <span className="text-3xl font-bold text-gray-900 dark:text-white">
                  R$ {price}
                </span>
                <span className="text-gray-500 dark:text-gray-400 ml-1">
                  /{billingCycle === 'annual' ? t('subscription.payment.perYear') : t('subscription.payment.perMonth')}
                </span>
              </div>
              {billingCycle === 'annual' && (
                <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-sm rounded-full">
                  {t('subscription.payment.savings')} R$ {pricing.annualSavings}
                </span>
              )}
            </div>
          </div>

          {/* AGC: Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* AGC: Payment Methods */}
          <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
            {t('subscription.payment.paymentMethod')}
          </h4>
          <div className="space-y-3 mb-6">
            {paymentMethods.map((method) => {
              const Icon = method.icon
              return (
                <button
                  key={method.id}
                  onClick={() => setSelectedMethod(method.id)}
                  className={`w-full p-4 rounded-lg border-2 flex items-center space-x-4 transition-all ${
                    selectedMethod === method.id
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className={`p-2 rounded-full ${
                    selectedMethod === method.id
                      ? 'bg-emerald-100 dark:bg-emerald-900/50'
                      : 'bg-gray-100 dark:bg-gray-800'
                  }`}>
                    <Icon className={`w-5 h-5 ${
                      selectedMethod === method.id
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-gray-500'
                    }`} />
                  </div>
                  <div className="text-left flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">{method.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{method.description}</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedMethod === method.id
                      ? 'border-emerald-500 bg-emerald-500'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}>
                    {selectedMethod === method.id && (
                      <CheckCircle className="w-4 h-4 text-white" />
                    )}
                  </div>
                </button>
              )
            })}
          </div>

          {/* AGC: Simulation Notice */}
          <div className="mb-6 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-sm text-amber-700 dark:text-amber-400 flex items-start space-x-2">
              <span className="text-lg">🧪</span>
              <span>
                <strong>Modo de Simulação:</strong> Nenhum pagamento real será processado. 
                Integração com Mercado Pago/PayPal em breve!
              </span>
            </p>
          </div>

          {/* AGC: Security Badge */}
          <div className="flex items-center justify-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-6">
            <Shield className="w-4 h-4 text-green-500" />
            <span>Pagamento seguro e criptografado</span>
          </div>

          {/* AGC: Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handlePayment}
              disabled={isProcessing}
              className={`w-full py-4 rounded-lg font-semibold text-white transition-all flex items-center justify-center space-x-2 ${
                tier === 'pro'
                  ? 'bg-blue-500 hover:bg-blue-600'
                  : 'bg-yellow-500 hover:bg-yellow-600'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Processando...</span>
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  <span>Simular Pagamento de R$ {price}</span>
                </>
              )}
            </button>
            <button
              onClick={onCancel}
              className="w-full py-3 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaymentSimulation

